import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Breadcrumb, Button, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import {
  AUDIO,
  IMAGE,
  MIN_ASSET_DURATION,
  MIN_PHOTOS_NUMBER,
  PAGE_SIZE,
  STOCK_CLIP,
  TEXT_OVERLAY,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { AuthContext } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES, TributeVideoBackgroundType } from '@/enums';
import { getTributeAssetsDurationValidation } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { Photos } from '@/pages/Event/TributeVideo/CreateVideo/Photos';
import { Review } from '@/pages/Event/TributeVideo/CreateVideo/Review';
import { ThemeComponent } from '@/pages/Event/TributeVideo/CreateVideo/Theme';
import { Music } from '@/pages/Event/components/Music';
import { Setup } from '@/pages/Event/components/Setup';
import {
  RightChevronBoldIcon as ArrowRight,
  Events as Calendar,
  RightArrowIcon,
  XMarkIcon as XMark
} from '@/resources';
import {
  useCreateTributeVideoMutation,
  useGetEventByIdQuery,
  useGetEventTags,
  useGetEventTributeVideos,
  useGetTributeVideoQuery,
  useUpdateTributeVideo
} from '@/services';
import { TestIds } from '@/shared/test-ids';
import {
  Asset,
  CustomAttributeValue,
  Tag,
  ThemeProp,
  TributeVideoAsset,
  TributeVideoOptionsType,
  UpdateCreateTributeVideoAsset,
  UpdateCreateTributeVideoPayload
} from '@/types';

const AVERAGE_THEME_DURATION = 26;

export const CreateVideoComponent = () => {
  const { eventId, tributeVideoId } = useParams();
  const isEditPage = !!tributeVideoId;
  const { permissions } = useContext(AuthContext);
  const [t] = useTranslation('common');
  const [currentStepNumber, setCurrentStepNumber] = useState<number>(1);
  const [enableNext, setEnableNext] = useState<boolean>(false);
  const [chosenSongs, setChosenSongs] = useState<UpdateCreateTributeVideoAsset[]>([]);
  const [selectedSongs, setSelectedSongs] = useState<TributeVideoAsset[]>([]);
  const [theme, setTheme] = useState<ThemeProp | null>(null);
  const [videoId, setVideoId] = useState<number>();
  const [selectedPhotos, setSelectedPhotos] = useState<TributeVideoAsset[]>([]);
  const [chosenPhotos, setChosenPhotos] = useState<UpdateCreateTributeVideoAsset[]>([]);
  const [songDuration, setSongDuration] = useState<number>(0);
  const [allowReviewStep, setAllowReviewStep] = useState<boolean>(false);
  const [maxAssets, setMaxAssets] = useState<number | undefined>(0);
  const navigate = useNavigate();
  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);
  const [errorTributeVideo, setErrorTributeVideo] = useState<number>();
  const [errorEvent, setErrorEvent] = useState<number>();
  const [enabledSteps, setEnabledSteps] = useState<boolean[]>();
  const [tributeVideoName, setTributeVideoName] = useState<string>('');
  const [tributeVideoOptions, setTributeVideoOptions] = useState<TributeVideoOptionsType>({
    textsEnabled: false,
    imageBordersEnabled: false,
    introEnabled: false,
    outroEnabled: false,
    musicEnabled: true,
    barsColor: undefined,
    durationOfSingleImage: 0,
    backgroundType: TributeVideoBackgroundType.Motion
  });
  const [customAttributeValues, setCustomAttributeValues] = useState<CustomAttributeValue[]>([]);
  const [movementBundleId, setMovementBundleId] = useState<number | null>(null);
  const [hasMusicStep, setHasMusicStep] = useState<boolean>(true);
  const [activeSubStep, setActiveSubStep] = useState<{ label: string; isPending: boolean } | null>(
    null
  );
  const [subStepEnterRequestId, setSubStepEnterRequestId] = useState<number>(0);
  const [subStepExitRequestId, setSubStepExitRequestId] = useState<number>(0);

  useEffect(() => {
    window.onpopstate = () => {
      if (hasOrgAccess) {
        navigate(`/events/${eventId}`);
      } else {
        navigate(`/events/${eventId}/upload-content/requests`);
      }
    };
  }, [hasOrgAccess, eventId, navigate]);

  const {
    data: eventData,
    isLoading: isLoadingData,
    isError: isErrorEvent
  } = useGetEventByIdQuery(Number(eventId), {
    onSuccess: () => undefined,
    onError: (error) => setErrorEvent(error.status)
  });

  const {
    data: tributeVideoData,
    refetch: tributeVideoDataRefetch,
    isLoading: isLoadingTributeVideoData,
    isError: isErrorTributeVideo
  } = useGetTributeVideoQuery(
    Number(tributeVideoId),
    { onSuccess: () => undefined, onError: (error) => setErrorTributeVideo(error.status) },
    !!eventData
  );

  const shouldShowMaxAssetsLoader =
    (((currentStepNumber === 2 || currentStepNumber === 3) &&
      songDuration !== 0 &&
      !hasOrgAccess &&
      !eventData?.data.relatedUsersCanGenerate &&
      hasMusicStep) ||
      (currentStepNumber === 3 &&
        songDuration !== 0 &&
        (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) &&
        hasMusicStep)) &&
    maxAssets === 0;

  const showSpinner =
    isLoadingData ||
    shouldShowMaxAssetsLoader ||
    (isEditPage && !isErrorEvent && isLoadingTributeVideoData);

  useEffect(() => {
    if (tributeVideoData && tributeVideoData.data.eventId.toString() !== eventId) {
      navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', eventId ? eventId : ''));
    }

    const tributeVideoSongs = tributeVideoData?.data.assets.filter(
      (asset: TributeVideoAsset) => asset.asset.assetType === AUDIO
    );

    if (tributeVideoSongs?.length > 0) {
      const totalDuration = tributeVideoSongs?.reduce((total: number, song: TributeVideoAsset) => {
        return total + (song?.asset?.duration || 0);
      }, 0);
      setSongDuration(totalDuration);
    }
    if (tributeVideoSongs && currentStepNumber === 1) {
      setSelectedSongs(
        tributeVideoSongs?.map((asset: TributeVideoAsset) => ({
          id: asset.id,
          tributeVideoId: asset.tributeVideoId,
          asset: asset?.asset,
          order: selectedSongs.length + 1,
          durationMultiplier:
            asset.durationMultiplier && asset.durationMultiplier > 0 ? asset.durationMultiplier : 1
        }))
      );
    }
    const videoTheme = tributeVideoData?.data?.videoTheme;
    const customVideoTheme = tributeVideoData?.data?.customVideoTheme;
    if (videoTheme) {
      setTheme({
        ...videoTheme,
        themeSource: 'public' as const,
        themeKey: `${videoTheme.id}-public`
      });
    } else if (customVideoTheme) {
      setTheme({
        ...customVideoTheme,
        themeSource: 'custom' as const,
        themeKey: `${customVideoTheme.id}-custom`
      });
    }
    const tributeVideoPhotos =
      tributeVideoData?.data.assets.filter(
        (asset: TributeVideoAsset) =>
          asset.asset.assetType === IMAGE ||
          asset.asset.assetType === VIDEO ||
          asset.asset.assetType === TEXT_OVERLAY ||
          asset.asset.assetType === STOCK_CLIP
      ) ?? [];
    if (tributeVideoPhotos !== 0 && currentStepNumber === 1) {
      setSelectedPhotos(tributeVideoPhotos);
    }
  }, [tributeVideoData]);

  useEffect(() => {
    if (selectedSongs?.length !== 0) {
      setChosenSongs(
        selectedSongs.map((asset) => ({
          id: asset.id ?? 0,
          tributeVideoId: asset.tributeVideoId ?? 0,
          assetId: asset.asset?.id,
          order: selectedSongs.length + 1,
          durationMultiplier:
            asset.durationMultiplier && asset.durationMultiplier > 0 ? asset.durationMultiplier : 1
        }))
      );
    }
  }, [selectedSongs]);

  useEffect(() => {
    setChosenSongs([]);
  }, [hasMusicStep]);

  useEffect(() => {
    if (selectedPhotos?.length === 0) {
      setChosenPhotos([]);
    } else {
      setChosenPhotos(() => [
        ...selectedPhotos.map((asset, index) => ({
          id: asset.id ?? 0,
          tributeVideoId: asset.tributeVideoId ?? 0,
          assetId: asset.asset.id,
          order: asset.order ?? index + 1,
          durationMultiplier:
            asset.durationMultiplier && asset.durationMultiplier > 0 ? asset.durationMultiplier : 1
        }))
      ]);
    }
  }, [selectedPhotos]);

  useEffect(() => {
    if (
      [1, 2, 3].includes(currentStepNumber) &&
      songDuration !== 0 &&
      !hasOrgAccess &&
      !eventData?.data.relatedUsersCanGenerate &&
      hasMusicStep
    ) {
      setMaxAssets(Math.floor((songDuration - AVERAGE_THEME_DURATION) / MIN_ASSET_DURATION));
    } else if (
      [1, 2, 3].includes(currentStepNumber) &&
      songDuration !== 0 &&
      (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) &&
      hasMusicStep
    ) {
      setMaxAssets(Math.floor((songDuration - (theme?.duration ?? 0)) / MIN_ASSET_DURATION));
    } else if (!hasMusicStep) {
      setMaxAssets(undefined);
    }
  }, [songDuration, theme, currentStepNumber, hasMusicStep, selectedSongs]);

  const { mutate: createTributeVideo } = useCreateTributeVideoMutation(queryClient, {
    onSuccess: (response) => {
      setVideoId(response?.data.id);
      setCustomAttributeValues(response?.data.customAttributeValues);
      hasOrgAccess
        ? navigate(`/events/${eventId}/create-video/${response?.data.id}`)
        : navigate(`/events/${eventId}/upload-content/tribute-videos/create/${response?.data.id}`);
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutate: updateTributeVideo } = useUpdateTributeVideo(queryClient, {
    onSuccess: () => {
      tributeVideoDataRefetch();
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  useEffect(() => {
    let stepsToEnable: boolean[] = [];
    if (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) {
      stepsToEnable = [true, true];
    } else {
      stepsToEnable = [true];
    }

    if (hasMusicStep) {
      if (selectedSongs.length > 0) {
        stepsToEnable.push(true);
        if (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) {
          if (theme) {
            stepsToEnable.push(true);
            if (
              selectedPhotos.length >= (hasMusicStep ? 0 : MIN_PHOTOS_NUMBER) &&
              selectedPhotos.length < (maxAssets ?? 0)
            ) {
              stepsToEnable.push(true);
            } else {
              stepsToEnable.push(false);
            }
          } else {
            stepsToEnable.push(...Array(2).fill(false));
          }
        } else {
          if (
            (selectedPhotos.length >= 1 && selectedPhotos.length < (maxAssets ?? 0)) ||
            allowReviewStep
          ) {
            stepsToEnable.push(true);
          } else {
            stepsToEnable.push(false);
          }
        }
      } else {
        stepsToEnable.push(...Array(3).fill(false));
      }
    } else {
      if (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) {
        if (theme) {
          stepsToEnable.push(true);
          if (selectedPhotos.length >= MIN_PHOTOS_NUMBER) {
            stepsToEnable.push(true);
          } else {
            stepsToEnable.push(false);
          }
        } else {
          stepsToEnable.push(...Array(2).fill(false));
        }
      } else {
        if (selectedPhotos.length >= MIN_PHOTOS_NUMBER) {
          stepsToEnable.push(true);
        } else {
          stepsToEnable.push(false);
        }
      }
    }

    if (
      stepsToEnable.length > 0 &&
      !getTributeAssetsDurationValidation(selectedPhotos, selectedSongs, theme)
    ) {
      stepsToEnable[stepsToEnable.length - 1] = false;
    }

    setEnabledSteps(stepsToEnable);
  }, [theme, selectedSongs, hasMusicStep, selectedPhotos, maxAssets]);

  const steps = (() => {
    if (!hasOrgAccess && !eventData?.data.relatedUsersCanGenerate) {
      const limitedSteps = [];
      if (hasMusicStep) {
        limitedSteps.push(t('music'));
      }
      limitedSteps.push(t('photos'), t('review'));
      return limitedSteps;
    }

    const baseSteps = [t('setup')];

    if (hasMusicStep) {
      baseSteps.push(t('music'));
    }

    if (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) {
      baseSteps.push(t('theme'), t('photos'), t('review'));
    } else {
      baseSteps.push(t('photos'), t('review'));
    }
    return baseSteps;
  })();
  const { data: allTags } = useGetEventTags(eventId as string);

  const tributeVideoTags = allTags?.data
    ?.filter((tag: Tag) => tag.name === TRIBUTE_VIDEO)
    .map((tag: Tag) => `${tag.id}`);
  const { data: allAssets } = useGetEventTributeVideos(
    eventId as string,
    PAGE_SIZE,
    tributeVideoTags?.length === 0 ? ['-1'] : tributeVideoTags
  );

  const isThemeStepForCurrentFlow =
    (eventData?.data.relatedUsersCanGenerate || hasOrgAccess) &&
    ((hasMusicStep && currentStepNumber === 3) || (!hasMusicStep && currentStepNumber === 2));

  const onNext = (wizardNumberClicked = 0) => {
    if (wizardNumberClicked === 0 && isThemeStepForCurrentFlow && activeSubStep?.isPending) {
      setSubStepEnterRequestId((prev) => prev + 1);
      return;
    }

    if (
      wizardNumberClicked === 0 &&
      isThemeStepForCurrentFlow &&
      activeSubStep &&
      !activeSubStep.isPending
    ) {
      setActiveSubStep(null);
    }

    const tributeVideoIdNumber = tributeVideoId ? Number(tributeVideoId) : 0;
    const videoIdNumber = videoId || 0;

    const eventIdNumber = Number(eventId);

    let assets: UpdateCreateTributeVideoAsset[] = [];
    if (chosenSongs && chosenPhotos.length === 0 && hasMusicStep) {
      assets = chosenSongs;
    } else if (chosenPhotos.length !== 0 && hasMusicStep) {
      assets = [...chosenSongs, ...chosenPhotos];
    } else if (!hasMusicStep) {
      assets = chosenPhotos;
    }

    const previewVideoUrl = tributeVideoData?.data.previewVideoUrl
      ? tributeVideoData.data.previewVideoUrl.split('?')[0]
      : null;

    const tributeVideos = allAssets?.pages.flatMap((page) =>
      page.data?.items.filter((item: Asset & { name: string }) =>
        ['Tribute Video', 'Diaporama', 'Huldevideo'].some((substring) =>
          item.name?.includes(substring)
        )
      )
    );
    const videoThemeId =
      theme?.isPublish === undefined
        ? null
        : theme?.id || tributeVideoData?.data.videoThemeId || null;
    const customVideoThemeId =
      theme?.isPublish === undefined
        ? theme?.id || tributeVideoData?.data.customVideoThemeId || null
        : null;
    const lastTributeVideoName = tributeVideos?.[tributeVideos?.length - 1]?.name;
    const numberInTributeVideoName = lastTributeVideoName?.match(/\d+/g)?.pop() ?? 0;
    const uploaderTributeVideoName = t('tributeVideoWithNumber', {
      number: (Number(numberInTributeVideoName) ?? 0) + 1
    });

    const tributeVideo: UpdateCreateTributeVideoPayload = {
      id: tributeVideoIdNumber || videoIdNumber,
      name:
        !hasOrgAccess && !eventData?.data.relatedUsersCanGenerate
          ? uploaderTributeVideoName
          : tributeVideoName,
      eventId: eventIdNumber,
      assets,
      previewVideoUrl,
      videoThemeId,
      customVideoThemeId,
      ...(!hasOrgAccess && {
        tags: [
          {
            name: TRIBUTE_VIDEO
          }
        ]
      }),
      tributeVideoAssetId: tributeVideoData?.data?.tributeVideoAsset?.id || null,
      maxAssetsCount: maxAssets,
      options: tributeVideoOptions ?? tributeVideoData?.data.options,
      customAttributeValues:
        currentStepNumber === 1
          ? customAttributeValues
          : tributeVideoData?.data.customAttributeValues,
      movementBundleId: movementBundleId
    };

    if (currentStepNumber === 1 && !tributeVideoId) {
      enableNext && createTributeVideo(tributeVideo);
    } else {
      updateTributeVideo(tributeVideo);
    }

    if (wizardNumberClicked === 0) {
      if (currentStepNumber === steps.length) {
        if (hasOrgAccess) {
          navigate(`/events/${eventId}`);
        } else {
          navigate(`/events/${eventId}/upload-content/tribute-videos`);
        }
      }
    }

    if (wizardNumberClicked === 0) {
      enableNext && setCurrentStepNumber(currentStepNumber + 1);
    } else {
      setActiveSubStep(null);
      setCurrentStepNumber(wizardNumberClicked);
    }
  };

  const onBack = () => {
    if (isThemeStepForCurrentFlow && activeSubStep && !activeSubStep.isPending) {
      setSubStepExitRequestId((prev) => prev + 1);
      return;
    }
    setActiveSubStep(null);
    setCurrentStepNumber(currentStepNumber - 1);
  };

  const isThemePage = currentStepNumber === 2;

  const wizardSteps = steps.flatMap((label, index) => {
    const items: Array<{ label: string; index: number; isSubStep: boolean; isPending: boolean }> = [
      { label, index, isSubStep: false, isPending: false }
    ];
    if (activeSubStep && index === currentStepNumber - 1) {
      items.push({
        label: activeSubStep.label,
        index,
        isSubStep: true,
        isPending: activeSubStep.isPending
      });
    }
    return items;
  });

  const isSubStepActive = !!activeSubStep && !activeSubStep.isPending;

  const canProceedNext =
    enableNext || (Boolean(activeSubStep?.isPending) && isThemeStepForCurrentFlow);

  const stepGroups = steps.map((label, index) => ({
    label,
    index,
    subSteps: wizardSteps.filter((step) => step.index === index && step.isSubStep),
    isMainActive:
      currentStepNumber === index + 1 && !(isSubStepActive && index === currentStepNumber - 1)
  }));
  const content = () => {
    switch (currentStepNumber) {
      case 1:
        return !eventData?.data.relatedUsersCanGenerate && !hasOrgAccess ? (
          <Music
            setEnableNext={setEnableNext}
            selectedSongs={selectedSongs}
            setSelectedSongs={setSelectedSongs}
            isTab={false}
            isCreateVideo
          />
        ) : (
          <Setup
            eventTypeId={eventData?.data.eventTypeId}
            setHasMusicStep={setHasMusicStep}
            setEnableNext={setEnableNext}
            setTributeVideoName={setTributeVideoName}
            setCustomAttributeValues={setCustomAttributeValues}
            setMovementBundleId={setMovementBundleId}
            setTributeVideoOptions={setTributeVideoOptions}
            tributeVideoData={tributeVideoData?.data}
            organizationId={eventData?.data?.organization?.id}
          />
        );
      case 2:
        return hasMusicStep && (hasOrgAccess || eventData?.data.relatedUsersCanGenerate) ? (
          <Music
            setEnableNext={setEnableNext}
            selectedSongs={selectedSongs}
            setSelectedSongs={setSelectedSongs}
            isTab={false}
            isCreateVideo
            setAllowReviewStep={setAllowReviewStep}
          />
        ) : eventData?.data.relatedUsersCanGenerate || hasOrgAccess ? (
          <ThemeComponent
            tributeVideoOptions={tributeVideoOptions}
            setEnableNext={setEnableNext}
            setVideoTheme={setTheme}
            videoTheme={theme}
            onSubStepChange={setActiveSubStep}
            subStepEnterRequestId={subStepEnterRequestId}
            subStepExitRequestId={subStepExitRequestId}
            organizationId={eventData?.data?.organization?.id}
          />
        ) : (
          <Photos
            theme={theme as ThemeProp}
            setEnableNext={setEnableNext}
            id={eventId ?? ''}
            setSelectedPhotos={setSelectedPhotos}
            selectedPhotos={selectedPhotos ?? tributeVideoData?.data?.assets}
            assets={tributeVideoData?.data?.assets}
            minAssets={1}
            maxAssets={maxAssets}
            durationOfSingleImage={
              hasMusicStep ? undefined : tributeVideoData?.data?.options?.durationOfSingleImage
            }
            organizationId={eventData?.data?.organization?.id}
          />
        );
      case 3:
        return hasMusicStep && (hasOrgAccess || eventData?.data.relatedUsersCanGenerate) ? (
          eventData?.data.relatedUsersCanGenerate || hasOrgAccess ? (
            <ThemeComponent
              tributeVideoOptions={tributeVideoOptions}
              setEnableNext={setEnableNext}
              setVideoTheme={setTheme}
              videoTheme={theme}
              onSubStepChange={setActiveSubStep}
              subStepEnterRequestId={subStepEnterRequestId}
              subStepExitRequestId={subStepExitRequestId}
              organizationId={eventData?.data?.organization?.id}
            />
          ) : (
            <Photos
              theme={theme as ThemeProp}
              setEnableNext={setEnableNext}
              id={eventId ?? ''}
              setSelectedPhotos={setSelectedPhotos}
              selectedPhotos={selectedPhotos ?? tributeVideoData?.data?.assets}
              assets={tributeVideoData?.data?.assets}
              minAssets={hasMusicStep ? 0 : MIN_PHOTOS_NUMBER}
              maxAssets={maxAssets}
              durationOfSingleImage={
                hasMusicStep ? undefined : tributeVideoData?.data?.options?.durationOfSingleImage
              }
              organizationId={eventData?.data?.organization?.id}
            />
          )
        ) : eventData?.data.relatedUsersCanGenerate || hasOrgAccess ? (
          <Photos
            theme={theme as ThemeProp}
            setEnableNext={setEnableNext}
            id={eventId ?? ''}
            setSelectedPhotos={setSelectedPhotos}
            selectedPhotos={selectedPhotos ?? tributeVideoData?.data?.assets}
            assets={tributeVideoData?.data?.assets}
            minAssets={hasMusicStep ? 0 : MIN_PHOTOS_NUMBER}
            maxAssets={maxAssets}
            durationOfSingleImage={
              hasMusicStep ? undefined : tributeVideoData?.data?.options?.durationOfSingleImage
            }
            organizationId={eventData?.data?.organization?.id}
          />
        ) : (
          <Review
            relatedUsersCanGenerate={eventData?.data.relatedUsersCanGenerate}
            assets={tributeVideoData?.data?.assets}
            tributeVideoData={tributeVideoData?.data}
            titleText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[0].value : undefined
            }
            deceasedNameText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[1].value : undefined
            }
            birthDeathText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[2].value : undefined
            }
            hasMusicStep={hasMusicStep}
            tributeVideoOptions={tributeVideoOptions}
          />
        );
      case 4:
        return hasMusicStep ? (
          eventData?.data.relatedUsersCanGenerate || hasOrgAccess ? (
            <Photos
              theme={theme as ThemeProp}
              setEnableNext={setEnableNext}
              id={eventId ?? ''}
              setSelectedPhotos={setSelectedPhotos}
              selectedPhotos={selectedPhotos ?? tributeVideoData?.data?.assets}
              assets={tributeVideoData?.data?.assets}
              minAssets={hasMusicStep ? 0 : MIN_PHOTOS_NUMBER}
              maxAssets={maxAssets}
              durationOfSingleImage={
                hasMusicStep ? undefined : tributeVideoData?.data?.options?.durationOfSingleImage
              }
              organizationId={eventData?.data?.organization?.id}
            />
          ) : (
            <Review
              relatedUsersCanGenerate={eventData?.data.relatedUsersCanGenerate || hasOrgAccess}
              theme={theme as ThemeProp}
              assets={tributeVideoData?.data?.assets}
              tributeVideoData={tributeVideoData?.data}
              titleText={
                tributeVideoOptions.textsEnabled ? customAttributeValues[0].value : undefined
              }
              deceasedNameText={
                tributeVideoOptions.textsEnabled ? customAttributeValues[1].value : undefined
              }
              birthDeathText={
                tributeVideoOptions.textsEnabled ? customAttributeValues[2].value : undefined
              }
              hasMusicStep={hasMusicStep}
              tributeVideoOptions={tributeVideoOptions}
            />
          )
        ) : (
          <Review
            relatedUsersCanGenerate={eventData?.data.relatedUsersCanGenerate || hasOrgAccess}
            theme={theme as ThemeProp}
            assets={tributeVideoData?.data?.assets}
            tributeVideoData={tributeVideoData?.data}
            titleText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[0].value : undefined
            }
            deceasedNameText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[1].value : undefined
            }
            birthDeathText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[2].value : undefined
            }
            hasMusicStep={hasMusicStep}
            tributeVideoOptions={tributeVideoOptions}
          />
        );
      case 5:
        return (
          <Review
            relatedUsersCanGenerate={eventData?.data.relatedUsersCanGenerate || hasOrgAccess}
            theme={theme as ThemeProp}
            assets={tributeVideoData?.data?.assets}
            tributeVideoData={tributeVideoData?.data}
            titleText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[0].value : undefined
            }
            deceasedNameText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[1].value : undefined
            }
            birthDeathText={
              tributeVideoOptions.textsEnabled ? customAttributeValues[2].value : undefined
            }
            hasMusicStep={hasMusicStep}
            tributeVideoOptions={tributeVideoOptions}
          />
        );
    }
  };

  return (
    <>
      {showSpinner ? (
        <div className='flex h-full items-center justify-center'>
          <Spinner />
        </div>
      ) : errorTributeVideo || errorEvent ? (
        <ErrorPage code={Number(errorEvent ?? errorTributeVideo)} />
      ) : (
        !isErrorTributeVideo &&
        !isErrorEvent && (
          <div className='flex size-full flex-col md:mt-7 md:px-20'>
            {hasOrgAccess && (
              <div className='mb-12 hidden items-center justify-between md:flex'>
                <Breadcrumb
                  className='text-lg'
                  icon={<Calendar className='size-4 text-primary-blue' />}
                  iconPath={'/events'}
                  path={[
                    { label: eventData?.data?.name, url: `/events/${eventData?.data?.id}` },
                    { label: t('createVideo') }
                  ]}
                />
                <Button
                  variant={ButtonVariant.TERTIARY}
                  handleOnClick={() => {
                    if (hasOrgAccess) {
                      navigate(`/events/${eventData?.data?.id}`);
                    } else {
                      navigate(`/events/${eventData?.data?.id}/upload-content/requests`);
                    }
                  }}
                  className='flex flex-row-reverse items-center'>
                  <XMark className='ml-2' />
                  {t('cancel')}
                </Button>
              </div>
            )}
            {!hasOrgAccess && (
              <div className='mb-12 hidden items-center justify-end md:flex'>
                <Button
                  variant={ButtonVariant.TERTIARY}
                  handleOnClick={() =>
                    navigate(`/events/${eventData?.data?.id}/upload-content/requests`)
                  }
                  className='flex flex-row-reverse items-center'>
                  <XMark className='ml-2' />
                  {t('cancel')}
                </Button>
              </div>
            )}
            <div className='md:scrollable-content mt-2 flex-1 overflow-y-scroll md:mt-0 md:scrollbar-none'>
              <div className='mt-1 flex w-full flex-row justify-center gap-1 md:mb-12 md:mt-0 md:gap-3'>
                {stepGroups.map((step, i) => (
                  <div key={`main-${step.index}`} className='flex flex-row items-center'>
                    <div className='flex flex-col items-center'>
                      <div
                        onClick={() => {
                          if (enabledSteps?.[step.index] && currentStepNumber !== step.index + 1) {
                            onNext(step.index + 1);
                          }
                        }}
                        className={`size-9 rounded-full md:size-14 ${
                          step.isMainActive
                            ? 'bg-primary-black font-bold text-white'
                            : enabledSteps?.[step.index]
                              ? 'cursor-pointer bg-secondary-pale-blue-gray text-primary-black'
                              : 'bg-gray-100 text-gray-300'
                        } flex items-center justify-center text-xs md:text-2xl`}>
                        {step.index + 1}
                      </div>
                      <p
                        className={`mt-3 text-xs md:text-base ${
                          step.isMainActive
                            ? 'font-bold text-primary-black'
                            : 'text-secondary-steel-blue'
                        }`}>
                        {step.label}
                      </p>
                    </div>
                    {i !== stepGroups.length - 1 && (
                      <div className='relative -mt-8 ml-3 flex h-6 w-4 items-center md:w-16'>
                        <div className='h-0.5 w-full bg-secondary-pale-blue-gray' />
                        {step.subSteps.length > 0 && (
                          <div className='absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1'>
                            {step.subSteps.map((subStep, subStepIndex) => (
                              <div
                                key={`sub-${step.index}-${subStepIndex}`}
                                className={`flex size-5 items-center justify-center rounded-full text-2xs font-bold md:size-6 md:text-xs ${
                                  subStep.isPending
                                    ? 'border border-secondary-steel-blue bg-white text-secondary-steel-blue'
                                    : 'bg-primary-black text-white'
                                }`}>
                                {`${step.index + 1}.${subStepIndex + 1}`}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div
                className={`${!isThemePage && 'mb-20'} mt-7 flex-1 ${hasOrgAccess ? 'px-2' : 'pl-1'}`}>
                {content()}
              </div>
              <div className='fixed bottom-0 right-0 z-10 flex h-16 w-full items-center justify-center bg-transparent'>
                <div className='md:absolute md:right-0 md:top-3 md:mr-20'>
                  <div
                    className='flex h-full flex-row gap-x-4 rounded-md p-1.5 text-base md:text-xl'
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}>
                    {currentStepNumber !== 1 && (
                      <div
                        className='flex cursor-pointer flex-row items-center'
                        onClick={onBack}
                        data-testid={TestIds.tributeVideo.backButton}>
                        <RightArrowIcon className='mr-2 rotate-180' /> {t('back')}
                      </div>
                    )}
                    <div className='h-8 border-l border-solid border-gray-500' />
                    <div
                      data-testid={TestIds.tributeVideo.nextButton}
                      className={`flex flex-row items-center ${
                        canProceedNext
                          ? 'cursor-pointer text-primary-blue'
                          : 'disabled cursor-default text-secondary-gray'
                      } font-black`}
                      onClick={() => (canProceedNext ? onNext() : undefined)}>
                      {currentStepNumber === steps.length ? t('tributeVideos') : t('next')}
                      <ArrowRight className='ml-2' />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};
